import type { CellRange, GridApi, ProcessCellForExportParams } from 'ag-grid-community';
import { isValidElement } from 'react';
import type { ReactNode } from 'react';
import { sanitizeNumericInput } from './numericInput';
import type { BombiixCellType, BombiixColumnDef, BombiixRow, BombiixSelectCellProps, BombiixSelectOption } from './types';

export const CLIPBOARD_LIST_SEPARATOR = ', ';

/** Keys that open AG Grid's own cell editor when pressed on a highlighted cell. */
const EDITOR_START_KEYS: ReadonlySet<string> = new Set(['Enter', 'F2']);

/** The cell types whose paste goes through the cell's own input rule rather than AG Grid's parser. */
const PASTE_RULED_CELL_TYPES: ReadonlySet<BombiixCellType> = new Set(['checkbox', 'text', 'currency', 'percent', 'date']);

/** Clipboard texts a checkbox reads as ticked and as cleared. */
const TRUE_TEXTS: ReadonlySet<string> = new Set(['true', 'yes', 'y', '1']);
const FALSE_TEXTS: ReadonlySet<string> = new Set(['false', 'no', 'n', '0', '']);

/** The stored form of a date cell's value. */
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The props the text-like cells read to limit what they accept. */
interface InputCellProps {
  type?: 'text' | 'number' | 'digits';
  maxLength?: number;
  allowNegative?: boolean;
  decimals?: number;
}

/** The prop a read-only rendered cell builds its content from. */
interface AccessorCellProps<TRow> {
  accessor?: (row: TRow) => ReactNode;
}

/**
 * A column's cell props for a row: the static props overlaid with the per-row ones.
 * @param colDef - the column.
 * @param row - the row, when known.
 * @returns the merged props.
 */
function cellPropsOf<TRow extends BombiixRow>(colDef: BombiixColumnDef<TRow>, row: TRow | undefined): Record<string, unknown> {
  return { ...(colDef.cellProps ?? {}), ...(row && colDef.getCellProps ? colDef.getCellProps(row) : {}) };
}

/**
 * An option's label, by the same rule as SelectInputCell.
 * @param props - the select cell props.
 * @param option - the option.
 * @returns the label.
 */
function labelOf(props: BombiixSelectCellProps, option: BombiixSelectOption): string {
  return props.getOptionLabel ? props.getOptionLabel(option) : String(option?.label ?? option?.name ?? '');
}

/**
 * An option's comparable key, by the same rule as SelectInputCell.
 * @param props - the select cell props.
 * @param option - the option.
 * @returns the key.
 */
function keyOf(props: BombiixSelectCellProps, option: BombiixSelectOption): string {
  return props.getOptionValue ? props.getOptionValue(option) : String(option?.value ?? option?._id ?? '');
}

/**
 * Label for one stored entry, looked up in the options and falling back to the entry itself.
 * @param props - the select cell props.
 * @param entry - a stored id or option object.
 * @returns the label to copy.
 */
function entryLabel(props: BombiixSelectCellProps, entry: unknown): string {
  const isObject = entry !== null && typeof entry === 'object';
  const key = isObject ? keyOf(props, entry as BombiixSelectOption) : String(entry);
  const option = (props.options ?? []).find((candidate) => keyOf(props, candidate) === key);
  if (option) return labelOf(props, option);
  return isObject ? labelOf(props, entry as BombiixSelectOption) : key;
}

/**
 * The option whose label (case-insensitive) or key equals a pasted part.
 * @param props - the select cell props.
 * @param part - one pasted label.
 * @returns the option, or undefined when none matches.
 */
function findOption(props: BombiixSelectCellProps, part: string): BombiixSelectOption | undefined {
  const wanted = part.toLowerCase();
  return (props.options ?? []).find((option) => labelOf(props, option).trim().toLowerCase() === wanted || keyOf(props, option) === part);
}

/**
 * The text a value that is not a React element stands for: an object by its name, label or
 * keyName, anything else as it prints.
 * @param value - the stored value.
 * @returns the text to copy.
 */
function plainText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    const named = value as { name?: unknown; label?: unknown; keyName?: unknown };
    return String(named.name ?? named.label ?? named.keyName ?? '');
  }
  return String(value);
}

/**
 * The option labels a select cell shows, joined for the clipboard.
 * @param colDef - the select column.
 * @param row - the row the value belongs to, for per-row options.
 * @param value - the stored id, option object, or array of either.
 * @returns the labels, or '' when the cell is empty.
 */
export function selectValueToText<TRow extends BombiixRow>(colDef: BombiixColumnDef<TRow>, row: TRow | undefined, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const props = cellPropsOf(colDef, row) as BombiixSelectCellProps;
  const entries: unknown[] = Array.isArray(value) ? value : [value];
  return entries
    .map((entry) => entryLabel(props, entry))
    .filter(Boolean)
    .join(CLIPBOARD_LIST_SEPARATOR);
}

/**
 * Resolves pasted labels to the option(s) a pick in the dropdown would produce. All labels must
 * match an option of the target row, otherwise the cell keeps its current value. A label that
 * itself contains the list separator ("ABC Textiles, Ltd") still matches as one label.
 * @param colDef - the select column.
 * @param row - the target row, whose options decide what is valid.
 * @param text - the pasted text.
 * @param currentValue - the cell's value before the paste.
 * @returns the matched option, the array of matched options, or `currentValue`.
 */
export function selectTextToValue<TRow extends BombiixRow>(colDef: BombiixColumnDef<TRow>, row: TRow | undefined, text: string, currentValue: unknown): unknown {
  const props = cellPropsOf(colDef, row) as BombiixSelectCellProps;
  const matched = splitListLabels(text, CLIPBOARD_LIST_SEPARATOR, (label) => findOption(props, label));
  if (!matched.length) return props.isMulti ? [] : currentValue;
  if (matched.some((option) => !option)) return currentValue;
  if (props.isMulti) return matched;
  return matched.length === 1 ? matched[0] : currentValue;
}

/**
 * The value a text-like cell accepts from a paste, by the rule its own input applies as you type:
 * a checkbox reads true/false words, a number or currency cell keeps only a well-formed decimal,
 * a digits cell only digits, a percent cell only digits, a point and a sign, a date cell only a
 * stored-format day, and any cell honours its `maxLength`.
 * @param cellType - the cell type.
 * @param text - the pasted text.
 * @param props - the cell props carrying the input's limits.
 * @returns the value to store, or undefined when the input would refuse the text.
 */
function acceptInputText(cellType: BombiixCellType, text: string, props: InputCellProps): unknown {
  const trimmed = text.trim();
  if (cellType === 'checkbox') {
    const lowered = trimmed.toLowerCase();
    if (TRUE_TEXTS.has(lowered)) return true;
    return FALSE_TEXTS.has(lowered) ? false : undefined;
  }
  if (cellType === 'date') return trimmed === '' || isCalendarDay(trimmed) ? trimmed : undefined;
  const { type, maxLength, allowNegative, decimals } = props;
  let accepted = trimmed;
  if (cellType === 'currency' || (cellType === 'text' && type === 'number')) accepted = sanitizeNumericInput(trimmed, { allowNegative, decimals });
  else if (cellType === 'percent') accepted = trimmed.replace(/[^0-9.-]/g, '');
  else if (cellType === 'text' && type === 'digits') accepted = trimmed.replace(/[^0-9]/g, '');
  if (accepted === '' && trimmed !== '') return undefined;
  return typeof maxLength === 'number' && maxLength > 0 ? accepted.slice(0, maxLength) : accepted;
}

/**
 * Fill handle value for one target cell: the dragged values repeated in order, never a number
 * series. Arrays are cloned so the filled rows never share one array instance. Typed on the two
 * fields it reads so grids on either AG Grid version in the app can pass it as `setFillValue`.
 * @param params - AG Grid's fill operation params.
 * @returns the value to write, or `false` (AG Grid's own fill) when the drag carried no values.
 */
export function copyFillValue(params: { initialValues: unknown[]; currentIndex: number }): unknown {
  const { initialValues, currentIndex } = params;
  if (!initialValues.length) return false;
  const source = initialValues[currentIndex % initialValues.length];
  return Array.isArray(source) ? [...source] : source;
}

/**
 * Whether a key on a highlighted cell would open AG Grid's built-in text editor. Fillable columns
 * are AG-Grid-editable only so the fill handle can write them; that editor shows the raw stored
 * value and swallows Escape, so it must never open over the cell's own control. Delete is left to
 * AG Grid, which clears the selected range through the fill path; Backspace only on macOS, where
 * AG Grid treats it as a delete key — elsewhere it would start that editor.
 * @param event - the keydown event AG Grid is about to process.
 * @returns true when AG Grid must not handle the key.
 */
export function startsCellEditor(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target?.classList?.contains('ag-cell')) return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const { key } = event;
  if (key === 'Backspace') return !isMacPlatform();
  return EDITOR_START_KEYS.has(key) || key.length === 1;
}

/**
 * Rows covered by a cell range, top to bottom. Pinned rows are never part of a fill source.
 * @param api - the grid the range belongs to.
 * @param range - the range a fill was dragged from.
 * @returns the row data in the range.
 */
export function rowsInRange<TRow>(api: GridApi<TRow>, range: CellRange | undefined): TRow[] {
  const { startRow, endRow } = range ?? {};
  if (!startRow || !endRow || startRow.rowPinned || endRow.rowPinned) return [];
  const first = Math.min(startRow.rowIndex, endRow.rowIndex);
  const last = Math.max(startRow.rowIndex, endRow.rowIndex);
  const rows: TRow[] = [];
  for (let i = first; i <= last; i += 1) {
    const data = api.getDisplayedRowAtIndex(i)?.data;
    if (data) rows.push(data);
  }
  return rows;
}

/**
 * Plain text of a rendered value. A truncated value with a hover tooltip copies the tooltip's full
 * text, not the shortened label.
 * @param node - a string, number, element or list of them.
 * @returns the text the node stands for.
 */
export function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map((child: ReactNode) => nodeText(child)).join('');
  if (!isValidElement(node)) return '';
  const { overlay, children } = node.props as { overlay?: ReactNode; children?: ReactNode };
  if (isValidElement(overlay)) return nodeText((overlay.props as { children?: ReactNode }).children);
  return nodeText(children);
}

/**
 * Clipboard text for a copied cell: what the cell shows. A column's own `getClipboardText` wins;
 * select cells copy their option labels; a cell rendered from an `accessor` copies that rendering's
 * text; a stored object copies its name; everything else keeps AG Grid's formatted value.
 * @param params - AG Grid's clipboard export params.
 * @param leafDefs - the grid's leaf Bombiix column defs keyed by colId.
 * @returns the text to place on the clipboard.
 */
export function clipboardTextOf<TRow extends BombiixRow>(params: ProcessCellForExportParams<TRow>, leafDefs: Map<string, BombiixColumnDef<TRow>>): unknown {
  const colDef = leafDefs.get(params.column.getColId());
  const row = params.node?.data ?? undefined;
  if (colDef?.getClipboardText && row) return colDef.getClipboardText(row);
  if (colDef?.cellType === 'select') return selectValueToText(colDef, row, params.value);
  const { accessor } = colDef && row ? (cellPropsOf(colDef, row) as AccessorCellProps<TRow>) : {};
  if (typeof accessor === 'function' && row) return nodeText(accessor(row));
  if (isValidElement(params.value)) return nodeText(params.value);
  if (Array.isArray(params.value)) return params.value.map(plainText).join(CLIPBOARD_LIST_SEPARATOR);
  if (params.value !== null && typeof params.value === 'object') return plainText(params.value);
  return params.column.getColDef().useValueFormatterForExport === false ? params.value : params.formatValue(params.value);
}

/**
 * Value written by a paste, by the rule of the cell it lands in. Select cells resolve the pasted
 * labels against the target row's own options, text-like cells apply their input's limits, and
 * both keep the current value when the text is refused; other cells keep AG Grid's parsed value.
 * @param params - AG Grid's clipboard import params.
 * @param leafDefs - the grid's leaf Bombiix column defs keyed by colId.
 * @returns the value to write into the cell.
 */
export function clipboardValueOf<TRow extends BombiixRow>(params: ProcessCellForExportParams<TRow>, leafDefs: Map<string, BombiixColumnDef<TRow>>): unknown {
  const colDef = leafDefs.get(params.column.getColId());
  const row = params.node?.data ?? undefined;
  const currentValue = row && colDef?.field ? row[String(colDef.field)] : undefined;
  if (colDef?.cellType === 'select') return selectTextToValue(colDef, row, String(params.value ?? ''), currentValue);
  if (colDef?.cellType && PASTE_RULED_CELL_TYPES.has(colDef.cellType)) {
    const accepted = acceptInputText(colDef.cellType, String(params.value ?? ''), cellPropsOf(colDef, row) as InputCellProps);
    return accepted === undefined ? currentValue : accepted;
  }
  return params.column.getColDef().useValueParserForImport === false ? params.value : params.parseValue(params.value);
}

/**
 * Whether AG Grid treats Backspace as a delete key that clears the selected range: on macOS and
 * iOS / iPadOS, by the same platform test AG Grid applies. Everywhere else Backspace would start
 * its text editor instead.
 * @returns true on a platform where AG Grid reads Backspace as Delete.
 */
function isMacPlatform(): boolean {
  return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
}

/**
 * Splits pasted list text into labels and resolves each one, keeping a label whole when it itself
 * contains the separator: from each position the longest run of parts that resolves wins. Blank
 * parts are skipped.
 * @param text - the pasted text.
 * @param separator - the separator a copy joined the labels with.
 * @param resolve - finds the option for one label, or undefined when none matches.
 * @returns one entry per label, undefined where a label matched nothing.
 */
export function splitListLabels<TOption>(text: string, separator: string, resolve: (label: string) => TOption | undefined): Array<TOption | undefined> {
  const parts = text.split(separator);
  const resolved: Array<TOption | undefined> = [];
  let start = 0;
  while (start < parts.length) {
    let end = parts.length;
    let option: TOption | undefined;
    while (end > start) {
      const label = parts.slice(start, end).join(separator).trim();
      option = label ? resolve(label) : undefined;
      if (option !== undefined) break;
      end -= 1;
    }
    if (option !== undefined) {
      resolved.push(option);
      start = end;
    } else {
      if (parts[start]?.trim()) resolved.push(undefined);
      start += 1;
    }
  }
  return resolved;
}

/**
 * Whether text is a real day in the stored `yyyy-mm-dd` form. The round trip through Date refuses
 * days a month does not have (2024-02-30), which Date.parse alone accepts by rolling them over.
 * @param text - the trimmed text.
 * @returns true for a real calendar day.
 */
export function isCalendarDay(text: string): boolean {
  if (!ISO_DAY_PATTERN.test(text) || Number.isNaN(Date.parse(text))) return false;
  return new Date(text).toISOString().slice(0, 10) === text;
}
